import axios from 'axios';
import qs from 'qs';
import { mainStore } from 'partners-widget-core';
import checkBeforeTransform from '@/utils/checkBeforeTransform';

/**
 * @param {Date} startDate
 * @param {string} url
 */
const logDiffTime = (startDate, url) => {
  const end = new Date().getTime();
  // eslint-disable-next-line no-console
  console.log(`axios: [GET] ${url} for ${end - startDate.getTime()} ms`);
};

export default class HttpClient {
  constructor() {
    this.apiUrl = mainStore.store.baseUrl;
    this.subscribers = [];
    this.isRefreshing = false;
  }

  refresh(refreshToken) {
    return axios.post(`${this.apiUrl}/api/v1/oauth/auth/`, {
      refresh_token: refreshToken,
      client_id: mainStore.store.env.clientId,
      client_secret: mainStore.store.env.clientSecret,
      grant_type: 'refresh_token',
    }).then(({ data }) => {
      mainStore.setAccessToken(data.access_token);
      mainStore.setRefreshToken(data.refresh_token);
    });
  }

  setInterceptors(axiosInstance) {
    axiosInstance.interceptors.response.use(
      response => response,

      async (error) => {
        const request = error.config;

        if (axios.isCancel(error)) {
          return Promise.reject(error);
        }

        if (!error.response || (error.response?.status === 401 ? error.response.config.url.includes('oauth/auth') : true)) {
          if (error?.response?.status === 401) {
            try {
              const requestData = JSON.parse(request.data);
              if (requestData.grant_type === 'refresh_token') {
                throw new Error('Bad refresh token');
              }
            } catch (err) {
              mainStore.logout();
              return Promise.reject(err);
            }
          }

          return Promise.reject(error);
        }

        if (error.response.status === 401) {
          try {
            const refreshToken = mainStore.store.refreshToken;
            if (refreshToken) {
              if (!this.isRefreshing) {
                this.isRefreshing = true;
                try {
                  await this.refresh(refreshToken);
                  const token = mainStore.store.accessToken;
                  this.subscribers.map(item => item(token));
                  this.subscribers = [];
                  request.headers.Authorization = `Bearer ${token}`;
                  return axios(request);
                } catch (err) {
                  throw new Error('Bad refresh token');
                } finally {
                  this.isRefreshing = false;
                }
              } else {
                return new Promise((resolve) => {
                  this.subscribers.push((token) => {
                    if (token) {
                      request.headers.Authorization = `Bearer ${token}`;
                    }
                    resolve(axios(request));
                  });
                });
              }
            } else {
              throw new Error('Bad refresh token');
            }
          } catch (err) {
            mainStore.logout();
            return Promise.reject(err);
          }
        }

        return Promise.reject(error);
      },
    );
  }

  /**
     * Возвращает инстанс аксиоса
     * @return {AxiosInstance}
     */
  mainHttp() {
    const headers = {
      'x-oauth-client-id': mainStore.store.env.clientId,
    };

    if (mainStore.store.accessToken) {
      headers.Authorization = `Bearer ${mainStore.store.accessToken}`;
    }

    const axiosInstance = axios.create({
      baseURL: this.apiUrl,
      withCredentials: false,
      headers,
    });

    if (this.cookie) {
      axiosInstance.defaults.headers.Cookie = this.cookie;
    }

    this.setInterceptors(axiosInstance);

    return axiosInstance;
  }

  /**
     * GET запрос
     * @template T
     * @param {string} path - путь запроса
     * @param {Object} payload - данные запроса
     * @return {string|Promise<unknown>|Promise<T>|any}
     */
  get(path, payload = {}) {
    const start = new Date();
    let paramsQuery = null;
    return this.mainHttp().request({
      method: 'GET',
      url: path,
      params: { ...payload, SESSID: 1 },
      transformResponse(data) {
        return checkBeforeTransform(data);
      },
      paramsSerializer(params) {
        paramsQuery = qs.stringify(params, { skipNulls: true });
        return paramsQuery;
      },
    })
      .finally(() => {
        if (process.server) {
          let url = path;
          if (paramsQuery) url += `?${paramsQuery}`;
          logDiffTime(start, url);
        }
      });
  }

  /**
     * POST запрос
     * @template T
     * @param {string} path - путь запроса
     * @param {Object} payload - данные запроса
     * @param {Object} options - дополнительные параметры
     * @param {boolean} [options.isFormData=false] - нужно ли данные запроса трансформировать в FormData
     * @param {Object} [options.payloadParams={}] - дополнительные параметры запроса
     * @return {Promise<unknown>|Promise<T>|any}
     */
  post(path, payload = {}, options = {}) {
    const start = new Date();
    const {
      isFormData = false,
      payloadParams = {},
    } = options;

    const request = {
      method: 'POST',
      url: path,
      data: payload,
      params: { ...payloadParams, SESSID: 1 },
      transformResponse(data) {
        return checkBeforeTransform(data);
      },
    };

    if (isFormData) {
      request.transformRequest = data => qs.stringify(data, { skipNulls: true });
    }

    return this.mainHttp().request(request)
      .finally(() => {
        if (process.server) {
          logDiffTime(start, path);
        }
      });
  }

  /**
     * PUT запрос
     * @param {string} path - путь запроса
     * @param {Object} payload - данные запроса
     * @param {Object} options - дополнительные параметры
     * @param {boolean} [options.isFormData=false] - нужно ли данные запроса трансформировать в FormData
     * @param {Object} [options.payloadParams={}] - дополнительные параметры запроса
     * @return {any|Promise<AxiosResponse<any>>}
     */
  put(path, payload = {}, options = {}) {
    const {
      isFormData = false,
      payloadParams = {},
    } = options;

    const request = {
      method: 'PUT',
      url: path,
      data: payload,
      params: { ...payloadParams, SESSID: 1 },
      transformResponse(data) {
        return checkBeforeTransform(data);
      },
    };

    if (isFormData) {
      request.transformRequest = data => qs.stringify(data);
    }

    return this.mainHttp().request(request);
  }

  /**
     * DELETE запрос
     * @param {string} path - путь запроса
     * @param {Object} payload - данные запроса
     * @return {any|Promise<AxiosResponse<any>>}
     */
  delete(path, payload = {}) {
    return this.mainHttp().request({
      method: 'DELETE',
      url: path,
      data: payload,
      params: { SESSID: 1 },
      transformResponse(data) {
        return checkBeforeTransform(data);
      },
    });
  }

  /**
     * Запрос скачивания файла в формате blob
     * @param {string} path - путь запроса
     * @param {Object} payload - данные запроса
     * @return {Promise<AxiosResponse<any>>}
     */
  download(path, payload = {}) {
    return this.mainHttp().request({
      method: 'GET',
      url: path,
      responseType: 'blob',
      params: { ...payload },
    });
  }

  /**
     * Запрос отправки файла в формате FormData
     * @param {string} path - путь запроса
     * @param {File} file - файл выбранный пользователем
     * @return {Promise<AxiosResponse<any>>}
     */
  upload(path, file) {
    const formData = new FormData();
    formData.append('file', file);

    return this.mainHttp().request({
      method: 'POST',
      url: path,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      data: formData,
    });
  }
}
